import { analyzeCategory } from '../../../utils/categoryClassifier';
import { CARE_MEDICATION_NATURAL_CAPTURE_CUE_PATTERN } from '../../../shared/careMedicationPatterns';
import { CARE_BEHAVIOR_REFUSAL_PATTERN } from '../../../shared/careSignalPatterns';

const normalizeCandidateCategory = (value = '') => {
  const candidate = String(value || '').trim();
  return candidate && candidate !== 'unknown' ? candidate : null;
};

const normalizeConfidence = (value = '') => {
  const candidate = String(value || '').trim().toLowerCase();
  if (!candidate) {
    return 'none';
  }

  if (candidate === 'high' || candidate === 'medium' || candidate === 'low' || candidate === 'none') {
    return candidate;
  }

  return 'none';
};

const normalizePromotionText = (value = '') => String(value || '')
  .trim()
  .toLowerCase();

const hasMedicationCue = (text = '') => CARE_MEDICATION_NATURAL_CAPTURE_CUE_PATTERN.test(String(text || ''));

const hasActivityCue = (text = '') => /\b(?:activity|classroom|participated|joined|worked|played|recess|art class|music therapy|exercise|group work|project|school pickup|teacher reported|swimming|therapy session)\b/i.test(String(text || ''));

const looksLikeObservationalSummary = (text = '') => /\b(?:this week|again|after school|during daytime|noticeably|more tired|falling asleep|appetite(?:\s+also)?(?:\s+noticeably)? lower|harder to wake|sleeping faster)\b/i.test(String(text || ''));

const getPromotionReason = ({
  currentCategory = null,
  proposedCategory = null,
  text = '',
} = {}) => {
  const normalizedCurrent = normalizeCandidateCategory(currentCategory);
  const normalizedProposed = normalizeCandidateCategory(proposedCategory);
  const normalizedText = normalizePromotionText(text);

  if (!normalizedCurrent || !normalizedProposed || normalizedCurrent === normalizedProposed) {
    return 'no_promotion_needed';
  }

  if ((normalizedCurrent === 'behavior' || normalizedCurrent === 'mood') && normalizedProposed === 'food') {
    return 'preserve_behavior_or_mood_over_food';
  }

  if (normalizedCurrent === 'medication' && normalizedProposed === 'sleep') {
    return 'preserve_medication_over_sleep';
  }

  if (normalizedCurrent === 'activity' && normalizedProposed === 'sleep') {
    return 'preserve_activity_over_sleep';
  }

  if (
    normalizedCurrent === 'food'
    && ['behavior', 'mood'].includes(normalizedProposed)
    && /\bafter school\b/i.test(normalizedText)
  ) {
    return 'after_school_non_food';
  }

  if (normalizedCurrent === 'sleep' && normalizedProposed === 'medication' && hasMedicationCue(normalizedText)) {
    return 'medication_over_sleep';
  }

  if (normalizedCurrent === 'sleep' && normalizedProposed === 'activity' && hasActivityCue(normalizedText)) {
    return 'activity_over_sleep';
  }

  return 'no_safe_promotion_rule';
};

const inferSafePromotionTarget = ({
  currentCategory = null,
  text = '',
} = {}) => {
  const normalizedCurrent = normalizeCandidateCategory(currentCategory);
  const normalizedText = normalizePromotionText(text);
  if (!normalizedCurrent || !normalizedText) {
    return null;
  }

  if (
    normalizedCurrent === 'food'
    && /\bafter school\b/i.test(normalizedText)
  ) {
    if (
      /\b(?:upset|frustrat(?:ed|ing)|cry(?:ing|ied)|support|calm(?:ed)? down|scream(?:ed|ing)?|yell(?:ed|ing)?|behavior)\b/i.test(normalizedText)
      || CARE_BEHAVIOR_REFUSAL_PATTERN.test(normalizedText)
    ) {
      return 'behavior';
    }

    if (/\b(?:tired|emotional|calm|anxious|worried|excited|sad|frustrat(?:ed|ing)|upset)\b/i.test(normalizedText)) {
      return 'mood';
    }
  }

  if (normalizedCurrent === 'sleep' && hasMedicationCue(normalizedText)) {
    return 'medication';
  }

  if (normalizedCurrent === 'sleep' && hasActivityCue(normalizedText) && !looksLikeObservationalSummary(normalizedText)) {
    return 'activity';
  }

  return null;
};

export const resolveNaturalCaptureCategoryPromotion = (inputText = '', {
  currentCategory = null,
  proposedCategory = null,
  confidence = 'none',
} = {}) => {
  const originalCategory = normalizeCandidateCategory(currentCategory);
  const candidateCategory = normalizeCandidateCategory(proposedCategory);
  const normalizedConfidence = normalizeConfidence(confidence);
  const highConfidence = normalizedConfidence === 'high';

  if (!candidateCategory) {
    return {
      finalCategory: originalCategory,
      promotionApplied: false,
      promotionReason: 'no_proposed_category',
    };
  }

  if (!originalCategory) {
    return {
      finalCategory: candidateCategory,
      promotionApplied: true,
      promotionReason: 'no_current_category',
    };
  }

  const inferredSafeTarget = inferSafePromotionTarget({
    currentCategory: originalCategory,
    text: inputText,
  });

  if (inferredSafeTarget && inferredSafeTarget !== originalCategory) {
    return {
      finalCategory: inferredSafeTarget,
      promotionApplied: true,
      promotionReason: getPromotionReason({
        currentCategory: originalCategory,
        proposedCategory: inferredSafeTarget,
        text: inputText,
      }),
    };
  }

  if (candidateCategory === originalCategory) {
    return {
      finalCategory: originalCategory,
      promotionApplied: false,
      promotionReason: 'already_matching',
    };
  }

  if (!highConfidence) {
    return {
      finalCategory: originalCategory,
      promotionApplied: false,
      promotionReason: 'low_confidence_preserve_current',
    };
  }

  const promotionReason = getPromotionReason({
    currentCategory: originalCategory,
    proposedCategory: candidateCategory,
    text: inputText,
  });

  if (promotionReason === 'preserve_behavior_or_mood_over_food'
    || promotionReason === 'preserve_medication_over_sleep'
    || promotionReason === 'preserve_activity_over_sleep') {
    return {
      finalCategory: originalCategory,
      promotionApplied: false,
      promotionReason,
    };
  }

  return {
    finalCategory: candidateCategory,
    promotionApplied: true,
    promotionReason,
  };
};

export const buildNaturalCaptureCategoryDecision = (inputText = '', {
  currentPathCategory = null,
  previousCategory = null,
  sourceMode = '',
  inputMode = '',
  parserMode = '',
  shapeHint = '',
  categoryHint = null,
  confidenceMetadata = null,
  categorySource = '',
} = {}) => {
  const analysis = analyzeCategory(inputText, {
    allowUnknown: true,
  });
  const currentPathAnalysis = analyzeCategory(inputText, {
    previousCategory,
    allowUnknown: true,
  });
  const proposedCategory = normalizeCandidateCategory(
    analysis.category
    || categoryHint
    || currentPathCategory
    || previousCategory
  );
  const currentCategory = normalizeCandidateCategory(currentPathCategory || categoryHint || previousCategory);
  const confidence = normalizeConfidence(analysis.confidence || confidenceMetadata?.confidence);
  const reason = Array.isArray(analysis.reason) ? analysis.reason : [];
  const signals = Array.isArray(analysis.matchedSignals) ? analysis.matchedSignals : [];
  const promotion = resolveNaturalCaptureCategoryPromotion(inputText, {
    currentCategory,
    proposedCategory,
    confidence,
  });

  return {
    proposedCategory,
    finalCategory: promotion.finalCategory || currentCategory || proposedCategory || null,
    promotionApplied: Boolean(promotion.promotionApplied),
    promotionReason: promotion.promotionReason || 'no_safe_promotion_rule',
    confidence,
    reason,
    signals,
    runnerUp: analysis.runnerUp?.category || null,
    originalCategory: currentCategory,
    currentPathCategory: currentCategory,
    previousCategory: normalizeCandidateCategory(previousCategory),
    categoryHint: normalizeCandidateCategory(categoryHint),
    categorySource,
    sourceMode,
    inputMode,
    parserMode,
    shapeHint,
    currentPathConfidence: normalizeConfidence(currentPathAnalysis.confidence || confidenceMetadata?.confidence),
    currentPathReason: Array.isArray(currentPathAnalysis.reason) ? currentPathAnalysis.reason : [],
    currentPathSignals: Array.isArray(currentPathAnalysis.matchedSignals) ? currentPathAnalysis.matchedSignals : [],
    agreesWithCurrentPath: Boolean(
      proposedCategory
      && currentCategory
      && proposedCategory === currentCategory
    ),
  };
};
